import { useEffect, useMemo, useState } from 'react';
import { ImageWrapper } from '@apps/components/CarCard';
import StyledImage from '@apps/components/Image';
import { CarType } from '@apps/containers/EvOverviewContainer/props';
import {
  ExpandGroupWrapper,
  ExpandWrapper,
} from '@apps/containers/EvOverviewContainer/styled';
import usePostMsgContext from '@apps/containers/WebviewProvider/hooks';
import { useEvOverviewData } from '@apps/context/EvOverviewContext';
import { Money } from '@crudx/common';
import { TableColumnType } from '@crudx/mui';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import PushPinIcon from '@mui/icons-material/PushPin';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TableCell from '@mui/material/TableCell';
import Typography from '@mui/material/Typography';
import createBasePath from '@stack/helpers/lib/createBasePath';
import isEmpty from 'lodash/isEmpty';

import SortingIcon from './SortingIcon';

interface RenderHeaderColumnProps {
  headerTitle?: string;
  key?: string;
  onPress?: () => void;
}

export const useColumns = ({
  onChange,
  onReset,
  webView,
}: {
  onChange: (values?: CarType[]) => void;
  onReset: () => void;
  webView?: boolean;
}) => {
  // ====== HOOKS
  const [expanded, setExpanded] = useState<number[]>([]);
  const [isSort, setIsSort] = useState<boolean>(false);
  const { data, sorting, removeId, addId, setSorting } = useEvOverviewData();
  const { basePath } = createBasePath({ webView, type: 'cars' });
  // const store = useVariantStore().getState();
  // const addExpand = store.addEvExpand;
  // const removeExpand = store.removeEvExpand;
  const { clickAction } = usePostMsgContext();

  useEffect(() => {
    if (isEmpty(data)) return;
    onChange?.(data);
  }, [data]);

  const removeExpanded = (id: number) => {
    const newData = expanded.filter((i) => i !== id);
    setExpanded(newData);
  };

  const addExpanded = (id: number) => {
    if (expanded.includes(id)) {
      return;
    }
    const newData = [...expanded, id];
    setExpanded(newData);
  };

  const formatValue = (formatValue: string, values: unknown) => {
    if (isEmpty(values) || values === '0.00' || values === '0') return '-';
    return formatValue;
  };

  const onHandleSort = (key?: string) => {
    onReset();
    if (sorting?.key !== key) {
      setIsSort(true);
      setSorting({
        key,
        value: 'desc',
      });
      return;
    }
    setIsSort(!isSort);
    setSorting({
      key,
      value: sorting?.key === key && isSort ? 'asc' : 'desc',
    });
  };

  // const onClearSort = () => {
  //   onReset();
  //   setSorting({
  //     key: undefined,
  //     value: undefined,
  //   });
  // };

  const renderHeaderColumn = (props: RenderHeaderColumnProps) => {
    const { headerTitle, key, onPress } = props;
    const selectedColor = '#1E242F';
    const color = '#A8B5C9';
    const isSortedKey = key === sorting?.key;
    const colorUp =
      isSortedKey && sorting?.value === 'asc' ? selectedColor : color;
    const colorDown =
      isSortedKey && sorting?.value === 'desc' ? selectedColor : color;

    return (
      <TableCell
        sx={{
          '&.MuiTableCell-head': {
            borderTop: 'none !important',
          },
          height: '48.57px',
          cursor: 'pointer',
        }}
        valign="middle"
        onClick={() => {
          // if (isSortedKey) {
          //   onClearSort();
          //   return;
          // }
          onPress?.();
        }}
      >
        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'space-between'}
          spacing={1}
          sx={{
            marginBottom: '3px',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.75rem',
              fontWeight: 600,
            }}
          >
            {headerTitle}
          </Typography>

          <IconButton
            sx={{
              p: 0,
            }}
            disableRipple
            onClick={(event) => {
              event.stopPropagation();
              onPress?.();
            }}
          >
            <SortingIcon colorUp={colorUp} colorDown={colorDown} />
          </IconButton>
        </Stack>
      </TableCell>
    );
  };

  // ====== COLUMNS
  const column: TableColumnType<CarType>[] = useMemo(() => {
    const checkExpand = (item: number) => {
      return expanded.includes(item);
    };
    return [
      {
        key: 'car_model',
        title: 'Car Model',
        // minWidth: 250,
        dataColumnProps: {
          sx: {
            paddingLeft: '.75rem !important',
          },
        },
        sticky: true,
        render: (_, item) => {
          const thumb = item?.car_model_image;
          const id = item?.new_car_id;
          const isExpand = checkExpand(id);
          const isPinned = item?.is_pinned;
          const brand = item?.car_brand_slug;
          const slug = item?.new_car_slug;
          const newCarHref = `${basePath}/${brand}/${slug}`;
          const title = item.new_car_title;

          return (
            <Stack style={{ flexDirection: 'row', gap: 12 }}>
              <Box mx={-1}>
                <IconButton
                  onClick={() => {
                    onReset();
                    isPinned ? removeId(id?.toString()) : addId(id?.toString());
                  }}
                >
                  {isPinned ? (
                    <PushPinIcon
                      fontSize={'small'}
                      style={{ fill: '#31ACBF' }}
                    />
                  ) : (
                    <PushPinIcon
                      fontSize={'small'}
                      style={{ fill: '#A8B5C9', rotate: '45deg' }}
                    />
                  )}
                </IconButton>
              </Box>
              <Stack gap={5} direction={{ xs: 'column', md: 'row' }}>
                <a
                  href={newCarHref}
                  onClick={(e) => {
                    if (webView) {
                      e.preventDefault();
                      clickAction(newCarHref, false, {
                        title,
                        params: {
                          brand,
                          slug,
                        },
                      });
                    }
                  }}
                  style={{
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                  draggable={false}
                >
                  <ImageWrapper height={'35px'} width={'75px'}>
                    <StyledImage
                      alt={`${title} thumbnail image`}
                      width={70}
                      height={35}
                      src={thumb || ''}
                      fetchPriority="auto"
                      loading="lazy"
                      priority={false}
                    />
                  </ImageWrapper>
                </a>
                <Box sx={{ width: '100%' }}>
                  <a
                    href={newCarHref}
                    onClick={(e) => {
                      if (webView) {
                        e.preventDefault();
                        clickAction(newCarHref, false, {
                          title,
                          params: {
                            brand,
                            slug,
                          },
                        });
                      }
                    }}
                    style={{
                      textDecoration: 'none',
                      color: 'inherit',
                    }}
                    draggable={false}
                  >
                    <Typography
                      title={title}
                      fontWeight={'700'}
                      fontSize={16}
                      style={{
                        maxWidth: '120px',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {title || '-'}
                    </Typography>
                  </a>
                  {isExpand &&
                    item?.variants.map((variant, index) => {
                      return (
                        <Typography
                          title={variant.new_car_title}
                          key={`variant-title-${index}`}
                          fontWeight={'500'}
                          fontSize={14}
                          style={{
                            padding: '8px 0',
                            maxWidth: '120px',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            overflow: 'hidden',
                          }}
                        >
                          {variant.new_car_title || '-'}
                        </Typography>
                      );
                    })}
                  <Button
                    onClick={() => {
                      if (isExpand) {
                        removeExpanded(id);
                      } else {
                        addExpanded(id);
                      }
                    }}
                    color={'secondary'}
                    variant={'text'}
                    style={{
                      textTransform: 'none',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                    }}
                    fullWidth
                  >
                    <Typography
                      fontSize={12}
                      color={'#31ACBFE5'}
                      fontWeight={'bold'}
                    >
                      {'All Variants'}
                    </Typography>
                    {isExpand ? (
                      <KeyboardArrowUpIcon
                        style={{ color: '#31ACBFE5', fontSize: 12 }}
                      />
                    ) : (
                      <KeyboardArrowDownIcon
                        style={{ color: '#31ACBFE5', fontSize: 12 }}
                      />
                    )}
                  </Button>
                </Box>
              </Stack>
            </Stack>
          );
        },
      },
      {
        key: 'price',
        title: 'Price',
        minWidth: 130,
        headerColumnProps: {
          component: () =>
            renderHeaderColumn({
              key: 'sort_by_price',
              headerTitle: 'Price',
              onPress: () => {
                onHandleSort('sort_by_price');
              },
            }),
        },
        dataColumnProps: {
          sx: {
            position: 'relative',
          },
        },
        render: (_, item) => {
          const id = item?.new_car_id;
          const isExpand = checkExpand(id);
          const isZeroPrice = Number(item.price) === 0;
          return (
            <>
              {!isExpand && (
                <Typography fontSize={14} style={{ whiteSpace: 'nowrap' }}>
                  {isZeroPrice ? '-' : <Money amount={item.price} />}
                </Typography>
              )}
              <ExpandGroupWrapper>
                {isExpand &&
                  item?.variants.map((variant, index) => {
                    const isZeroVariantPrice = Number(variant.price) === 0;
                    return (
                      <ExpandWrapper key={`variant-price-${index}`}>
                        <Typography
                          fontSize={14}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {isZeroVariantPrice ? (
                            '-'
                          ) : (
                            <Money amount={variant.price} />
                          )}
                        </Typography>
                      </ExpandWrapper>
                    );
                  })}
              </ExpandGroupWrapper>
            </>
          );
        },
      },
      {
        key: 'mode',
        title: 'Driving Mode',
        minWidth: 140,
        headerColumnProps: {
          component: () =>
            renderHeaderColumn({
              key: 'sort_by_driving_mode',
              headerTitle: 'Driving Mode',
              onPress: () => {
                onHandleSort('sort_by_driving_mode');
              },
            }),
        },
        dataColumnProps: {
          sx: {
            position: 'relative',
          },
        },
        render: (_, item) => {
          const id = item?.new_car_id;
          const isExpand = checkExpand(id);
          return (
            <>
              {!isExpand && (
                <Typography fontSize={14} style={{ whiteSpace: 'nowrap' }}>
                  {item.driving_mode || '-'}
                </Typography>
              )}
              <ExpandGroupWrapper>
                {isExpand &&
                  item?.variants.map((variant, index) => {
                    return (
                      <ExpandWrapper key={`variant-mode-${index}`}>
                        <Typography
                          fontSize={14}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {item.driving_mode || '-'}
                        </Typography>
                      </ExpandWrapper>
                    );
                  })}
              </ExpandGroupWrapper>
            </>
          );
        },
      },
      {
        key: 'kmh',
        title: '0-100km/h',
        minWidth: 120,
        headerColumnProps: {
          component: () =>
            renderHeaderColumn({
              key: 'sort_by_acceleration',
              headerTitle: '0-100km/h',
              onPress: () => {
                onHandleSort('sort_by_acceleration');
              },
            }),
        },
        dataColumnProps: {
          sx: {
            position: 'relative',
          },
        },
        render: (_, item) => {
          const id = item?.new_car_id;
          const isExpand = checkExpand(id);
          const zeroTo100km = item?.acceleration;
          return (
            <>
              {!isExpand && (
                <Typography fontSize={14} style={{ whiteSpace: 'nowrap' }}>
                  {zeroTo100km && Number(zeroTo100km) != 0
                    ? `${zeroTo100km} s`
                    : '-'}
                </Typography>
              )}
              <ExpandGroupWrapper>
                {isExpand &&
                  item?.variants.map((variant, index) => {
                    return (
                      <ExpandWrapper key={`variant-mode-${index}`}>
                        <Typography
                          fontSize={14}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {variant?.acceleration &&
                          Number(variant?.acceleration) != 0
                            ? `${variant?.acceleration} s`
                            : '-'}
                        </Typography>
                      </ExpandWrapper>
                    );
                  })}
              </ExpandGroupWrapper>
            </>
          );
        },
      },
      {
        key: 'topSpeed',
        title: 'Top Speed',
        minWidth: 130,
        headerColumnProps: {
          component: () =>
            renderHeaderColumn({
              key: 'sort_by_top_speed',
              headerTitle: 'Top Speed',
              onPress: () => {
                onHandleSort('sort_by_top_speed');
              },
            }),
        },
        dataColumnProps: {
          sx: {
            position: 'relative',
          },
        },
        render: (_, item) => {
          const id = item?.new_car_id;
          const isExpand = checkExpand(id);
          return (
            <>
              {!isExpand && (
                <Typography fontSize={14} style={{ whiteSpace: 'nowrap' }}>
                  {formatValue(`${item.top_speed} km/h`, item.top_speed)}
                </Typography>
              )}
              <ExpandGroupWrapper>
                {isExpand &&
                  item?.variants.map((variant, index) => {
                    return (
                      <ExpandWrapper key={`variant-mode-${index}`}>
                        <Typography
                          fontSize={14}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {formatValue(
                            `${variant.top_speed} km/h`,
                            variant.top_speed
                          )}
                        </Typography>
                      </ExpandWrapper>
                    );
                  })}
              </ExpandGroupWrapper>
            </>
          );
        },
      },
      {
        key: 'range',
        title: 'Range (WLTP)',
        minWidth: 140,
        headerColumnProps: {
          component: () =>
            renderHeaderColumn({
              key: 'sort_by_max_range',
              headerTitle: 'Range (WLTP)',
              onPress: () => {
                onHandleSort('sort_by_max_range');
              },
            }),
        },
        dataColumnProps: {
          sx: {
            position: 'relative',
          },
        },
        render: (_, item) => {
          const id = item?.new_car_id;
          const isExpand = checkExpand(id);
          return (
            <>
              {!isExpand && (
                <Typography fontSize={14} style={{ whiteSpace: 'nowrap' }}>
                  {formatValue(`${item.max_range} km`, item.max_range)}
                </Typography>
              )}
              <ExpandGroupWrapper>
                {isExpand &&
                  item?.variants.map((variant, index) => {
                    return (
                      <ExpandWrapper key={`variant-mode-${index}`}>
                        <Typography
                          fontSize={14}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {formatValue(
                            `${variant.max_range} km`,
                            variant.max_range
                          )}
                        </Typography>
                      </ExpandWrapper>
                    );
                  })}
              </ExpandGroupWrapper>
            </>
          );
        },
      },
      {
        key: 'batteryCapacity',
        title: 'Battery Capacity',
        minWidth: 170,
        headerColumnProps: {
          component: () =>
            renderHeaderColumn({
              key: 'sort_by_battery_capacity',
              headerTitle: 'Battery Capacity',
              onPress: () => {
                onHandleSort('sort_by_battery_capacity');
              },
            }),
        },
        dataColumnProps: {
          sx: {
            position: 'relative',
          },
        },
        render: (_, item) => {
          const id = item?.new_car_id;
          const isExpand = checkExpand(id);
          return (
            <>
              {!isExpand && (
                <Typography fontSize={14} style={{ whiteSpace: 'nowrap' }}>
                  {formatValue(
                    `${item?.battery_capacity} kWh`,
                    item.battery_capacity
                  )}
                </Typography>
              )}
              <ExpandGroupWrapper>
                {isExpand &&
                  item?.variants.map((variant, index) => {
                    return (
                      <ExpandWrapper key={`variant-mode-${index}`}>
                        <Typography
                          fontSize={14}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {formatValue(
                            `${variant?.battery_capacity} kWh`,
                            variant.battery_capacity
                          )}
                        </Typography>
                      </ExpandWrapper>
                    );
                  })}
              </ExpandGroupWrapper>
            </>
          );
        },
      },
      {
        key: 'batteryType',
        title: 'Battery Type',
        minWidth: 100,
        headerColumnProps: {
          component: () =>
            renderHeaderColumn({
              key: 'sort_by_battery_type',
              headerTitle: 'Battery Type',
              onPress: () => {
                onHandleSort('sort_by_battery_type');
              },
            }),
        },
        dataColumnProps: {
          sx: {
            position: 'relative',
          },
        },
        render: (_, item) => {
          const id = item?.new_car_id;
          const isExpand = checkExpand(id);
          return (
            <>
              {!isExpand && (
                <Typography fontSize={14} style={{ whiteSpace: 'nowrap' }}>
                  {item.battery_type ?? '-'}
                </Typography>
              )}
              <ExpandGroupWrapper>
                {isExpand &&
                  item?.variants.map((variant, index) => {
                    return (
                      <ExpandWrapper key={`variant-mode-${index}`}>
                        <Typography
                          fontSize={14}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {variant?.battery_type ?? '-'}
                        </Typography>
                      </ExpandWrapper>
                    );
                  })}
              </ExpandGroupWrapper>
            </>
          );
        },
      },
      {
        key: 'maxAc',
        title: 'Max Input AC (OBC)',
        minWidth: 175,
        headerColumnProps: {
          component: () =>
            renderHeaderColumn({
              key: 'sort_by_max_input_ac',
              headerTitle: 'Max Input AC (OBC)',
              onPress: () => {
                onHandleSort('sort_by_max_input_ac');
              },
            }),
        },
        dataColumnProps: {
          sx: {
            position: 'relative',
          },
        },
        render: (_, item) => {
          const id = item?.new_car_id;
          const isExpand = checkExpand(id);
          return (
            <>
              {!isExpand && (
                <Typography fontSize={14} style={{ whiteSpace: 'nowrap' }}>
                  {formatValue(`${item.max_input_ac} kW`, item.max_input_ac)}
                </Typography>
              )}
              <ExpandGroupWrapper>
                {isExpand &&
                  item?.variants.map((variant, index) => {
                    return (
                      <ExpandWrapper key={`variant-mode-${index}`}>
                        <Typography
                          fontSize={14}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {formatValue(
                            `${variant.max_input_ac} kW`,
                            variant.max_input_ac
                          )}
                        </Typography>
                      </ExpandWrapper>
                    );
                  })}
              </ExpandGroupWrapper>
            </>
          );
        },
      },
      {
        key: 'maxDc',
        title: 'Max Input DC',
        minWidth: 175,
        headerColumnProps: {
          component: () =>
            renderHeaderColumn({
              key: 'sort_by_max_input_dc',
              headerTitle: 'Max Input DC',
              onPress: () => {
                onHandleSort('sort_by_max_input_dc');
              },
            }),
        },
        dataColumnProps: {
          sx: {
            position: 'relative',
          },
        },
        render: (_, item) => {
          const id = item?.new_car_id;
          const isExpand = checkExpand(id);
          const maxInputDc = item.max_input_dc;
          return (
            <>
              {!isExpand && (
                <Typography fontSize={14} style={{ whiteSpace: 'nowrap' }}>
                  {formatValue(`${maxInputDc} kW`, maxInputDc)}
                </Typography>
              )}
              <ExpandGroupWrapper>
                {isExpand &&
                  item?.variants.map((variant, index) => {
                    return (
                      <ExpandWrapper key={`variant-mode-${index}`}>
                        <Typography
                          fontSize={14}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          {formatValue(
                            `${variant?.max_input_dc} kW`,
                            variant.max_input_dc
                          )}
                        </Typography>
                      </ExpandWrapper>
                    );
                  })}
              </ExpandGroupWrapper>
            </>
          );
        },
      },
    ] as TableColumnType<CarType>[];
  }, [expanded, webView, sorting]);
  return column;
};
